import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../../context/ThemeContext';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, vs } from 'react-syntax-highlighter/dist/esm/styles/prism';
//@ts-ignore
import debounce from 'lodash/debounce';
// @ts-ignore
import { Helmet } from 'react-helmet';

type EncodingType = 'Base64' | 'URL' | 'HTML' | 'Hex' | 'Binary' | 'ASCII' | 'Unicode' | 'JSON' | 'Escape/Unescape';
type QuoteType = 'single' | 'double' | 'backtick';

interface EscapePreview {
  original: string;
  escaped: string;
  description: string;
}

const EncodersDecoders: React.FC = () => {
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [input, setInput] = useState<string>('');
  const [output, setOutput] = useState<string>('');
  const [mode, setMode] = useState<'encode' | 'decode'>('encode');
  const [encodingType, setEncodingType] = useState<EncodingType>('Base64');
  const [quoteType, setQuoteType] = useState<QuoteType>('double');
  const [error, setError] = useState<string>('');
  const [escapePreview, setEscapePreview] = useState<EscapePreview[]>([]);

  // Base64 character set (64 characters)
  const base64Chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

  const encodeBase64 = (text: string): string => {
    try {
      // Convert string to binary
      const binary = Array.from(text)
        .map(char => char.charCodeAt(0).toString(2).padStart(8, '0'))
        .join('');

      // Pad binary to be divisible by 6
      const padding = (6 - (binary.length % 6)) % 6;
      const paddedBinary = binary + '0'.repeat(padding);

      // Convert 6-bit chunks to Base64
      let result = '';
      for (let i = 0; i < paddedBinary.length; i += 6) {
        const chunk = paddedBinary.slice(i, i + 6);
        const value = parseInt(chunk, 2);
        result += base64Chars[value];
      }

      // Add padding characters (=)
      const paddingChars = Math.floor(padding / 2);
      return result + '='.repeat(paddingChars);
    } catch (error) {
      throw new Error('Invalid input for Base64 encoding');
    }
  };

  const decodeBase64 = (text: string): string => {
    try {
      // Remove padding characters
      const base64 = text.replace(/=+$/, '');
      
      // Convert Base64 to binary
      const binary = Array.from(base64)
        .map(char => {
          const value = base64Chars.indexOf(char);
          if (value === -1) throw new Error('Invalid Base64 character');
          return value.toString(2).padStart(6, '0');
        })
        .join('');

      // Convert 8-bit chunks to characters
      let result = '';
      for (let i = 0; i < binary.length - 7; i += 8) {
        const chunk = binary.slice(i, i + 8);
        const charCode = parseInt(chunk, 2);
        result += String.fromCharCode(charCode);
      }

      return result;
    } catch (error) {
      throw new Error('Invalid Base64 input');
    }
  };

  const encodeURL = (text: string): string => {
    try {
      return encodeURIComponent(text);
    } catch (error) {
      throw new Error('Invalid input for URL encoding');
    }
  };

  const decodeURL = (text: string): string => {
    try {
      return decodeURIComponent(text);
    } catch (error) {
      throw new Error('Invalid URL encoded input');
    }
  };

  const encodeHTML = (text: string): string => {
    try {
      return text.replace(/[&<>"']/g, (char) => {
        const entities: { [key: string]: string } = {
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;'
        };
        return entities[char];
      });
    } catch (error) {
      throw new Error('Invalid input for HTML encoding');
    }
  };

  const decodeHTML = (text: string): string => {
    try {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = text;
      return textarea.value;
    } catch (error) {
      throw new Error('Invalid HTML encoded input');
    }
  };

  const encodeHex = (text: string): string => {
    try {
      return Array.from(text)
        .map(char => char.charCodeAt(0).toString(16).padStart(2, '0'))
        .join('');
    } catch (error) {
      throw new Error('Invalid input for Hex encoding');
    }
  };

  const decodeHex = (text: string): string => {
    try {
      return text.replace(/.{2}/g, hex => String.fromCharCode(parseInt(hex, 16)));
    } catch (error) {
      throw new Error('Invalid Hex input');
    }
  };

  const encodeBinary = (text: string): string => {
    try {
      return Array.from(text)
        .map(char => char.charCodeAt(0).toString(2).padStart(8, '0'))
        .join(' ');
    } catch (error) {
      throw new Error('Invalid input for Binary encoding');
    }
  };

  const decodeBinary = (text: string): string => {
    try {
      return text.split(' ')
        .map(bin => String.fromCharCode(parseInt(bin, 2)))
        .join('');
    } catch (error) {
      throw new Error('Invalid Binary input');
    }
  };

  const encodeASCII = (text: string): string => {
    try {
      return Array.from(text)
        .map(char => char.charCodeAt(0))
        .join(' ');
    } catch (error) {
      throw new Error('Invalid input for ASCII encoding');
    }
  };

  const decodeASCII = (text: string): string => {
    try {
      return text.split(' ')
        .map(code => String.fromCharCode(parseInt(code)))
        .join('');
    } catch (error) {
      throw new Error('Invalid ASCII input');
    }
  };

  const encodeUnicode = (text: string): string => {
    try {
      return Array.from(text)
        .map(char => '\\u' + char.charCodeAt(0).toString(16).padStart(4, '0'))
        .join('');
    } catch (error) {
      throw new Error('Invalid input for Unicode encoding');
    }
  };

  const decodeUnicode = (text: string): string => {
    try {
      return text.replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) => 
        String.fromCharCode(parseInt(hex, 16))
      );
    } catch (error) {
      throw new Error('Invalid Unicode input');
    }
  };

  const encodeJSON = (text: string): string => {
    try {
      // First parse the input to validate it's valid JSON
      const parsed = JSON.parse(text);
      // Then stringify it with proper formatting
      return JSON.stringify(parsed, null, 2);
    } catch (error) {
      throw new Error('Invalid JSON input');
    }
  };

  const decodeJSON = (text: string): string => {
    try {
      // First parse the input to validate it's valid JSON
      const parsed = JSON.parse(text);
      // Then stringify it in a minified format
      return JSON.stringify(parsed);
    } catch (error) {
      throw new Error('Invalid JSON input');
    }
  };

  const encodeString = (text: string): string => {
    try {
      let result = text;
      
      // Escape quotes based on selected quote type
      switch (quoteType) {
        case 'single':
          result = result.replace(/'/g, "\\'");
          break;
        case 'double':
          result = result.replace(/"/g, '\\"');
          break;
        case 'backtick':
          result = result.replace(/`/g, '\\`');
          break;
      }

      // Wrap in selected quote type
      switch (quoteType) {
        case 'single':
          return `'${result}'`;
        case 'double':
          return `"${result}"`;
        case 'backtick':
          return `\`${result}\``;
      }
    } catch (error) {
      throw new Error('Invalid input for string escaping');
    }
  };

  const decodeString = (text: string): string => {
    try {
      // Remove surrounding quotes
      const trimmed = text.trim();
      if (!trimmed) return '';

      let result = trimmed;
      const firstChar = trimmed[0];
      const lastChar = trimmed[trimmed.length - 1];

      // Check if string is properly quoted
      if ((firstChar === "'" && lastChar === "'") ||
          (firstChar === '"' && lastChar === '"') ||
          (firstChar === '`' && lastChar === '`')) {
        result = trimmed.slice(1, -1);
      }

      // Unescape quotes
      return result
        .replace(/\\'/g, "'")
        .replace(/\\"/g, '"')
        .replace(/\\`/g, '`');
    } catch (error) {
      throw new Error('Invalid escaped string input');
    }
  };

  // Generate escape sequence preview
  useEffect(() => {
    if (encodingType === 'Escape/Unescape' && input) {
      const previews: EscapePreview[] = [];
      const quotes = {
        single: { char: "'", escaped: "\\'", desc: 'Single quote' },
        double: { char: '"', escaped: '\\"', desc: 'Double quote' },
        backtick: { char: '`', escaped: '\\`', desc: 'Backtick' }
      };

      // Check for quotes that need escaping
      const quote = quotes[quoteType];
      if (input.includes(quote.char)) {
        previews.push({
          original: quote.char,
          escaped: quote.escaped,
          description: quote.desc
        });
      }

      setEscapePreview(previews);
    } else {
      setEscapePreview([]);
    }
  }, [input, encodingType, quoteType]);

  const handleConversion = useCallback(() => {
    setError('');
    try {
      let result = '';
      if (mode === 'encode') {
        switch (encodingType) {
          case 'Base64':
            result = encodeBase64(input);
            break;
          case 'URL':
            result = encodeURL(input);
            break;
          case 'HTML':
            result = encodeHTML(input);
            break;
          case 'Hex':
            result = encodeHex(input);
            break;
          case 'Binary':
            result = encodeBinary(input);
            break;
          case 'ASCII':
            result = encodeASCII(input);
            break;
          case 'Unicode':
            result = encodeUnicode(input);
            break;
          case 'JSON':
            result = encodeJSON(input);
            break;
          case 'Escape/Unescape':
            result = encodeString(input);
            break;
        }
      } else {
        switch (encodingType) {
          case 'Base64':
            result = decodeBase64(input);
            break;
          case 'URL':
            result = decodeURL(input);
            break;
          case 'HTML':
            result = decodeHTML(input);
            break;
          case 'Hex':
            result = decodeHex(input);
            break;
          case 'Binary':
            result = decodeBinary(input);
            break;
          case 'ASCII':
            result = decodeASCII(input);
            break;
          case 'Unicode':
            result = decodeUnicode(input);
            break;
          case 'JSON':
            result = decodeJSON(input);
            break;
          case 'Escape/Unescape':
            result = decodeString(input);
            break;
        }
      }
      setOutput(result);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An error occurred');
      setOutput('');
    }
  }, [input, mode, encodingType, quoteType]);

  // Create a debounced version of handleConversion
  const debouncedConversion = useCallback(
    debounce(handleConversion, 300),
    [handleConversion]
  );

  // Update output whenever input, mode, encodingType, or quoteType changes
  useEffect(() => {
    if (input) {
      debouncedConversion();
    } else {
      setOutput('');
      setError('');
    }
    return () => {
      debouncedConversion.cancel();
    };
  }, [input, mode, encodingType, quoteType, debouncedConversion]);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleClear = () => {
    setInput('');
    setOutput('');
    setError('');
  };

  const getLanguageForEncoding = (type: EncodingType): string => {
    switch (type) {
      case 'Base64':
        return 'text';
      case 'URL':
        return 'text';
      case 'HTML':
        return 'html';
      case 'Hex':
        return 'text';
      case 'Binary':
        return 'text';
      case 'ASCII':
        return 'text';
      case 'Unicode':
        return 'javascript';
      case 'JSON':
        return 'json';
      case 'Escape/Unescape':
        return 'javascript';
      default:
        return 'text';
    }
  };

  return (
    <>
      <Helmet>
        <title>Encoders/Decoders | Text Encoding Tools - Davinqi</title>
        <meta name="description" content="Encode and decode text in various formats with Davinqi Encoders/Decoders. Fast, privacy-friendly, and supports Base64, URL, and more." />
        <meta property="og:title" content="Encoders/Decoders | Text Encoding Tools - Davinqi" />
        <meta property="og:description" content="Encode and decode text in various formats with Davinqi Encoders/Decoders. Fast, privacy-friendly, and supports Base64, URL, and more." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://davinqi.com/text/encoders" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Encoders/Decoders | Text Encoding Tools - Davinqi" />
        <meta name="twitter:description" content="Encode and decode text in various formats with Davinqi Encoders/Decoders. Fast, privacy-friendly, and supports Base64, URL, and more." />
      </Helmet>
      <div className="absolute inset-0 flex flex-col bg-gray-50 dark:bg-gray-900">
        {/* Header */}
        <div className="flex-none p-4 text-center relative">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Encoders & Decoders
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Encode and decode text in various formats
          </p>
          <div className="flex justify-center space-x-4 mt-4">
            <button
              onClick={() => setMode('encode')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                mode === 'encode'
                  ? 'bg-blue-600 dark:bg-blue-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Encode
            </button>
            <button
              onClick={() => setMode('decode')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                mode === 'decode'
                  ? 'bg-blue-600 dark:bg-blue-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Decode
            </button>
          </div>
          <button
            onClick={toggleDarkMode}
            className="absolute top-4 right-4 p-2 rounded-lg bg-gray-200 dark:bg-gray-700 
                     hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-200"
          >
            {isDarkMode ? (
              <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-gray-900" fill="currentColor" viewBox="0 0 20 20">
                <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
              </svg>
            )}
          </button>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex gap-4 p-4 pl-20 overflow-hidden">
          {/* Left Panel - Input */}
          <div className="w-1/2 bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 flex flex-col min-h-0">
            <div className="flex-none flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Input</h2>
              <div className="space-x-2">
                <select
                  value={encodingType}
                  onChange={(e) => setEncodingType(e.target.value as EncodingType)}
                  className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white 
                           rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 
                           focus:ring-blue-500 focus:border-transparent"
                >
                  {['Base64', 'URL', 'HTML', 'Hex', 'Binary', 'ASCII', 'Unicode', 'JSON', 'Escape/Unescape'].map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleClear}
                  className="px-3 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 
                           transition-colors duration-200 text-sm"
                >
                  Clear
                </button>
              </div>
            </div>
            {encodingType === 'Escape/Unescape' && (
              <div className="flex-none mb-4">
                <select
                  value={quoteType}
                  onChange={(e) => setQuoteType(e.target.value as QuoteType)}
                  className="w-full p-2 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white 
                           rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 
                           focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="single">Single Quote (')</option>
                  <option value="double">Double Quote (")</option>
                  <option value="backtick">Backtick (`)</option>
                </select>
              </div>
            )}
            <div className="flex-1 min-h-0">
              <textarea
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  if (e.target.value) {
                    debouncedConversion();
                  } else {
                    setOutput('');
                    setError('');
                  }
                }}
                placeholder={`Enter text to ${mode}...`}
                className="h-full w-full p-4 font-mono text-sm bg-gray-50 dark:bg-gray-700 
                         text-gray-900 dark:text-white rounded-lg border border-gray-200 
                         dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-transparent
                         resize-none"
              />
            </div>
            {error && (
              <p className="flex-none mt-2 text-red-500 text-sm">{error}</p>
            )}
          </div>

          {/* Right Panel - Output */}
          <div className="w-1/2 bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 flex flex-col min-h-0">
            <div className="flex-none flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Output</h2>
              <button
                onClick={() => {
                  if (output) {
                    handleCopy(output);
                  }
                }}
                className="px-3 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 
                         transition-colors duration-200 text-sm"
              >
                Copy
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-auto">
              {output ? (
                <div className="h-full">
                  <SyntaxHighlighter
                    language={getLanguageForEncoding(encodingType)}
                    style={isDarkMode ? vscDarkPlus : vs}
                    customStyle={{
                      height: '100%',
                      margin: 0,
                      borderRadius: '0.5rem',
                      backgroundColor: isDarkMode ? '#1F2937' : '#F9FAFB'
                    }}
                  >
                    {output}
                  </SyntaxHighlighter>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500 dark:text-gray-400">
                  {error ? 'Invalid input' : 'Enter text to encode/decode'}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EncodersDecoders; 